/**
 * NeoCoreActividadPage — vista de uso de la app móvil.
 *
 * Estética Neocoop: cream, naranja, tipografía limpia. Minimalista.
 * Nada de dark mode. La app se ve como Neocoop se ve.
 *
 * Auto-refresh cada 30s.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Warning } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

// ─── Tipos ─────────────────────────────────────────────────────────

type Metrics = {
  base: number
  han_entrado: number
  dau: number
  wau: number
  mau: number
  sesiones: number
  ahora: number
}

type PersonaRow = {
  id: string
  nombre_completo: string | null
  no_asociado: string | null
  neo_id: string | null
  ultima_actividad: string | null
  last_sign_in_at: string | null
}

type FeedRow = {
  when_: string
  tipo: 'login' | 'solicitud_credito' | 'reporte_pago' | 'movimiento'
  nombre_completo: string | null
  no_asociado: string | null
  detalle: string | null
}

// ─── Helpers ───────────────────────────────────────────────────────

const initials = (name: string | null) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?'
  const mitad = Math.floor(parts.length / 2)
  return ((parts[0][0] || '') + (parts[mitad][0] || '')).toUpperCase()
}

/** Proper case, sin cortar. Todos los nombres importan. */
const properName = (name: string | null) => {
  if (!name) return 'Sin nombre'
  return name
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ')
}

const fmtRel = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000)
  if (diffMin < 1) return 'ahora'
  if (diffMin < 60) return `hace ${diffMin} min`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `hace ${diffH}h`
  const diffD = Math.round(diffH / 24)
  if (diffD < 30) return `hace ${diffD}d`
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', timeZone: 'America/Guatemala' })
}

// ─── Componente principal ──────────────────────────────────────────

export function NeoCoreActividadPage() {
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [online, setOnline] = useState<PersonaRow[]>([])
  const [feed, setFeed] = useState<FeedRow[]>([])
  const [zombis, setZombis] = useState<PersonaRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const prevFeedIds = useRef<Set<string>>(new Set())
  const [newIds, setNewIds] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setError(null)
    try {
      const [mRes, oRes, fRes, zRes] = await Promise.all([
        supabase.rpc('actividad_app_metricas'),
        supabase.rpc('actividad_app_online_ahora'),
        supabase.rpc('actividad_app_feed_24h'),
        supabase.rpc('actividad_app_zombis_7d'),
      ])
      if (mRes.error) throw mRes.error
      if (oRes.error) throw oRes.error
      if (fRes.error) throw fRes.error
      if (zRes.error) throw zRes.error

      const mRow = Array.isArray(mRes.data) ? mRes.data[0] : mRes.data
      setMetrics(mRow ?? null)
      setOnline((oRes.data ?? []) as PersonaRow[])
      const newFeed = (fRes.data ?? []) as FeedRow[]

      const currentIds = new Set(newFeed.map((r) => `${r.when_}-${r.tipo}-${r.no_asociado}`))
      const arrivals = new Set<string>()
      currentIds.forEach((id) => {
        if (!prevFeedIds.current.has(id)) arrivals.add(id)
      })
      if (arrivals.size > 0 && prevFeedIds.current.size > 0) {
        setNewIds(arrivals)
        setTimeout(() => setNewIds(new Set()), 3000)
      }
      prevFeedIds.current = currentIds

      setFeed(newFeed)
      setZombis((zRes.data ?? []) as PersonaRow[])
      setLastUpdate(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    const t = setInterval(() => void load(), 30_000)
    return () => clearInterval(t)
  }, [load])

  const pct = useMemo(() => {
    if (!metrics || !metrics.base) return { dau: 0, wau: 0, mau: 0, han: 0 }
    return {
      dau: Math.round((Number(metrics.dau) / metrics.base) * 100),
      wau: Math.round((Number(metrics.wau) / metrics.base) * 100),
      mau: Math.round((Number(metrics.mau) / metrics.base) * 100),
      han: Math.round((Number(metrics.han_entrado) / metrics.base) * 100),
    }
  }, [metrics])

  return (
    <div style={{
      padding: '32px 28px',
      maxWidth: 1200,
      margin: '0 auto',
    }}>
      <style>{`
        @keyframes activityLive {
          0%, 100% { box-shadow: 0 0 0 0 rgba(15,138,87,0.5); }
          50%      { box-shadow: 0 0 0 6px rgba(15,138,87,0);   }
        }
        @keyframes activityEnter {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes activityFlash {
          0%   { background: rgba(240,78,35,0.06); }
          100% { background: transparent; }
        }
      `}</style>

      {/* Header · calmo, tipográfico */}
      <div style={{ marginBottom: 32 }}>
        <p style={{
          margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
          color: 'var(--brand-700, #B8380F)', textTransform: 'uppercase',
        }}>Actividad · en vivo</p>
        <h1 style={{
          margin: '8px 0 0', fontSize: 30, fontWeight: 800, letterSpacing: -0.7,
          color: 'var(--ink, #111)', lineHeight: 1.1,
        }}>Uso de la app</h1>
        <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--ink-soft, #6b7280)' }}>
          <span style={{
            display: 'inline-block', width: 6, height: 6, borderRadius: 99,
            background: '#0f8a57', marginRight: 8, verticalAlign: 'middle',
            animation: 'activityLive 2s infinite',
          }} />
          Actualizado automático cada 30 seg{lastUpdate && ` · ${lastUpdate.toLocaleTimeString('es-GT', { timeZone: 'America/Guatemala' })}`}
          {' · '}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            style={{
              all: 'unset', cursor: loading ? 'default' : 'pointer',
              color: 'var(--brand)', fontWeight: 700,
              textDecoration: 'underline', textDecorationStyle: 'dotted',
              textUnderlineOffset: 3,
            }}
          >
            {loading ? 'cargando' : 'actualizar ahora'}
          </button>
        </p>
      </div>

      {error && (
        <div style={{
          padding: 12, borderRadius: 10, marginBottom: 20,
          background: 'rgba(163,45,45,0.08)', color: 'var(--danger, #a32d2d)',
          fontSize: 13, border: '1px solid rgba(163,45,45,0.15)',
        }}>
          <Warning size={16} weight="duotone" style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {error}
        </div>
      )}

      {/* Métricas en línea · 6 números elegantes con separadores sutiles */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 0,
        marginBottom: 40,
        background: 'var(--surface, #fff)',
        border: '1px solid var(--hairline, rgba(17,17,17,0.08))',
        borderRadius: 18,
        overflow: 'hidden',
      }}>
        <StatCell label="Online ahora"   value={metrics?.ahora ?? 0}       sub="últimos 5 min"   highlight />
        <StatCell label="Sesiones"       value={metrics?.sesiones ?? 0}    sub="JWT vigente"     />
        <StatCell label="DAU"            value={metrics?.dau ?? 0}         sub={`${pct.dau}% base`}    />
        <StatCell label="WAU"            value={metrics?.wau ?? 0}         sub={`${pct.wau}% base`}    />
        <StatCell label="MAU"            value={metrics?.mau ?? 0}         sub={`${pct.mau}% base`}    />
        <StatCell label="Base instalada" value={metrics?.base ?? 0}        sub={`${metrics?.han_entrado ?? 0} activados`} />
      </div>

      {/* Grid principal: Feed | Conectados */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: 20,
      }}>
        {/* Feed · última hora / 24h */}
        <div>
          <SectionHeader
            title="Actividad reciente"
            sub={`${feed.length} eventos en 24h`}
          />
          <div style={{
            background: 'var(--surface, #fff)',
            border: '1px solid var(--hairline, rgba(17,17,17,0.08))',
            borderRadius: 16,
            overflow: 'hidden',
          }}>
            {feed.length === 0 ? (
              <EmptyRow text="Todavía no hay eventos hoy" />
            ) : (
              feed.map((r) => {
                const id = `${r.when_}-${r.tipo}-${r.no_asociado}`
                return <FeedItem key={id} row={r} isNew={newIds.has(id)} />
              })
            )}
          </div>
        </div>

        {/* Conectados */}
        <div>
          <SectionHeader
            title="Conectados en última hora"
            sub={`${online.length} ${online.length === 1 ? 'persona' : 'personas'}`}
          />
          <div style={{
            background: 'var(--surface, #fff)',
            border: '1px solid var(--hairline, rgba(17,17,17,0.08))',
            borderRadius: 16,
            overflow: 'hidden',
          }}>
            {online.length === 0 ? (
              <EmptyRow text="Nadie activo ahora mismo" />
            ) : (
              online.map((p) => {
                const min = p.ultima_actividad
                  ? Math.round((Date.now() - new Date(p.ultima_actividad).getTime()) / 60000)
                  : 999
                const state = min < 5 ? 'live' : min < 15 ? 'activo' : 'reciente'
                return <ConectadoItem key={p.id} persona={p} state={state} />
              })
            )}
          </div>
        </div>
      </div>

      {/* Reactivar · solo si hay */}
      {zombis.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <SectionHeader
            title="Para reactivar"
            sub={`${zombis.length} sin volver hace más de 7 días`}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10,
          }}>
            {zombis.map((p) => (
              <ZombiCard key={p.id} persona={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Piezas ────────────────────────────────────────────────────────

function StatCell({
  label, value, sub, highlight,
}: {
  label: string
  value: number | string
  sub: string
  highlight?: boolean
}) {
  return (
    <div style={{
      padding: '18px 20px',
      borderRight: '1px solid var(--hairline, rgba(17,17,17,0.06))',
      borderBottom: '1px solid var(--hairline, rgba(17,17,17,0.06))',
    }}>
      <p style={{
        margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
        color: highlight ? 'var(--brand)' : 'var(--ink-soft, #6b7280)',
        textTransform: 'uppercase',
      }}>{label}</p>
      <p style={{
        margin: '4px 0 0',
        fontSize: 30, fontWeight: 800, letterSpacing: -1,
        color: 'var(--ink, #111)',
        fontVariant: 'tabular-nums' as const,
        lineHeight: 1.1,
      }}>{value}</p>
      <p style={{
        margin: '3px 0 0', fontSize: 11, color: 'var(--ink-soft, #6b7280)',
      }}>{sub}</p>
    </div>
  )
}

function SectionHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: 'var(--ink, #111)', letterSpacing: -0.2 }}>
        {title}
      </h2>
      <span style={{ fontSize: 11.5, color: 'var(--ink-soft, #6b7280)' }}>· {sub}</span>
    </div>
  )
}

function FeedItem({ row, isNew }: { row: FeedRow; isNew: boolean }) {
  const iconoMap = {
    login: '🔓',
    solicitud_credito: '💰',
    reporte_pago: '🧾',
    movimiento: '💸',
  } as const
  const labelMap = {
    login: 'inició sesión',
    solicitud_credito: 'solicitó un crédito',
    reporte_pago: 'reportó un pago',
    movimiento: 'movimiento',
  } as const
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '14px 16px',
      borderBottom: '1px solid var(--hairline, rgba(17,17,17,0.06))',
      animation: isNew ? 'activityEnter 400ms ease-out, activityFlash 2.5s ease-out' : undefined,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10,
        background: 'var(--brand-50, #FFF5F0)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 17,
        flexShrink: 0,
      }}>{iconoMap[row.tipo]}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13.5, color: 'var(--ink, #111)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          <strong style={{ fontWeight: 700 }}>{properName(row.nombre_completo)}</strong>
          <span style={{ color: 'var(--ink-soft, #6b7280)' }}> {labelMap[row.tipo]}</span>
        </div>
        {row.detalle && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-soft, #6b7280)', marginTop: 2 }}>
            {row.detalle}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-muted, #9ca3af)', fontVariant: 'tabular-nums' as const, whiteSpace: 'nowrap' }}>
        {fmtRel(row.when_)}
      </div>
    </div>
  )
}

function ConectadoItem({ persona, state }: { persona: PersonaRow; state: 'live' | 'activo' | 'reciente' }) {
  const stateInfo = {
    live:     { label: 'En vivo',  color: '#0f8a57', bg: 'rgba(15,138,87,0.10)' },
    activo:   { label: 'Activo',   color: '#0f8a57', bg: 'rgba(15,138,87,0.06)' },
    reciente: { label: 'Reciente', color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
  }
  const s = stateInfo[state]
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '14px 16px',
      borderBottom: '1px solid var(--hairline, rgba(17,17,17,0.06))',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 99,
        background: 'var(--brand-50, #FFF5F0)',
        color: 'var(--brand, #F04E23)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 800, flexShrink: 0,
      }}>{initials(persona.nombre_completo)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13.5, fontWeight: 700, color: 'var(--ink, #111)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{properName(persona.nombre_completo)}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-soft, #6b7280)', marginTop: 2, fontVariant: 'tabular-nums' as const }}>
          NEO-{persona.no_asociado || persona.neo_id} · {fmtRel(persona.ultima_actividad)}
        </div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 800, padding: '4px 8px', borderRadius: 4,
        background: s.bg, color: s.color, letterSpacing: 0.4,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap', flexShrink: 0,
        animation: state === 'live' ? 'activityLive 2s infinite' : undefined,
      }}>
        {s.label}
      </span>
    </div>
  )
}

function ZombiCard({ persona }: { persona: PersonaRow }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 14px',
      background: 'var(--surface, #fff)',
      border: '1px solid var(--hairline, rgba(17,17,17,0.08))',
      borderRadius: 12,
    }}>
      <div style={{
        width: 30, height: 30, borderRadius: 99,
        background: 'rgba(217,119,6,0.10)',
        color: '#d97706',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 800, flexShrink: 0,
      }}>{initials(persona.nombre_completo)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 700, color: 'var(--ink, #111)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{properName(persona.nombre_completo)}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-soft, #6b7280)', marginTop: 2 }}>
          NEO-{persona.no_asociado || persona.neo_id} · {fmtRel(persona.last_sign_in_at)}
        </div>
      </div>
    </div>
  )
}

function EmptyRow({ text }: { text: string }) {
  return (
    <div style={{
      padding: '40px 16px', textAlign: 'center',
      color: 'var(--ink-muted, #9ca3af)', fontSize: 13,
    }}>
      {text}
    </div>
  )
}
